import { Fragment, useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { apiGet, apiPatch } from '../api/client'
import Layout from '../components/Layout'

type Communication = {
  id: string
  customer_id: string | null
  channel: 'web_form' | 'email' | 'phone'
  from_name: string | null
  from_email: string | null
  from_phone: string | null
  subject: string | null
  body: string | null
  status: 'new' | 'read' | 'responded' | 'archived'
  created_at: string
  customer_first_name: string | null
  customer_last_name: string | null
}

const CHANNEL_LABELS: Record<string, string> = {
  web_form: 'Web form',
  email: 'Email',
  phone: 'Phone',
}

function CustomerBadge({ isNew }: { isNew: boolean }) {
  const style: React.CSSProperties = {
    display: 'inline-block',
    padding: '0.15rem 0.5rem',
    borderRadius: '9999px',
    fontSize: '0.72rem',
    fontWeight: 600,
    background: isNew ? '#fff7ed' : '#f0fdf4',
    color: isNew ? '#c2410c' : '#15803d',
    border: `1px solid ${isNew ? '#fed7aa' : '#bbf7d0'}`,
    whiteSpace: 'nowrap',
  }
  return <span style={style}>{isNew ? 'New customer' : 'Existing customer'}</span>
}

function StatusBadge({ status }: { status: Communication['status'] }) {
  const map: Record<Communication['status'], { bg: string; color: string; label: string }> = {
    new:       { bg: '#eff6ff', color: '#1d4ed8', label: 'New' },
    read:      { bg: '#f3f4f6', color: '#4b5563', label: 'Read' },
    responded: { bg: '#f0fdf4', color: '#15803d', label: 'Responded' },
    archived:  { bg: '#f3f4f6', color: '#9ca3af', label: 'Archived' },
  }
  const { bg, color, label } = map[status]
  return (
    <span style={{
      display: 'inline-block',
      padding: '0.15rem 0.5rem',
      borderRadius: '9999px',
      fontSize: '0.72rem',
      fontWeight: 600,
      background: bg,
      color,
    }}>
      {label}
    </span>
  )
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  borderBottom: '1px solid #e5e7eb',
  padding: '0.625rem 0.75rem',
  fontSize: '0.8rem',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}

export default function InboxPage() {
  const { token } = useAuth()
  const [items, setItems] = useState<Communication[]>([])
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  useEffect(() => {
    if (!token) return
    setIsFetching(true)
    apiGet<{ communications: Communication[] }>('/api/v1/communications', token)
      .then(({ communications }) => setItems(communications))
      .catch((err: unknown) => {
        setFetchError(err instanceof Error ? err.message : 'Unknown error')
      })
      .finally(() => setIsFetching(false))
  }, [token])

  const newCount = items.filter((i) => i.status === 'new').length

  async function markRead(id: string) {
    if (!token) return
    try {
      await apiPatch<{ communication: Communication }>(`/api/v1/communications/${id}/status`, token, { status: 'read' })
      setItems((prev) => prev.map((i) => i.id === id ? { ...i, status: 'read' } : i))
    } catch {
      // silently ignore — status badge stays as-is
    }
  }

  async function toggleExpand(item: Communication) {
    if (expandedId === item.id) {
      setExpandedId(null)
      return
    }
    setExpandedId(item.id)
    if (item.status === 'new') {
      await markRead(item.id)
    }
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  const colCount = 7

  return (
    <Layout inboxCount={newCount}>
      <h2 style={{ marginTop: 0, color: '#111827' }}>
        Inbox
        {newCount > 0 && (
          <span style={{ marginLeft: '0.6rem', fontSize: '0.9rem', color: '#6b7280', fontWeight: 400 }}>
            {newCount} new
          </span>
        )}
      </h2>

      {isFetching && <p style={{ color: '#6b7280' }}>Loading…</p>}
      {fetchError && <p style={{ color: '#dc2626' }}>Error: {fetchError}</p>}
      {!isFetching && items.length === 0 && !fetchError && (
        <p style={{ color: '#6b7280' }}>No inbound enquiries yet.</p>
      )}

      {items.length > 0 && (
        <div style={{ background: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                {['From', 'Subject', 'Channel', 'Customer', 'Status', 'Received', ''].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const isExpanded = expandedId === item.id
                return (
                  <Fragment key={item.id}>
                    <tr
                      onClick={() => toggleExpand(item)}
                      style={{
                        background: item.status === 'new' ? '#fafbff' : '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      <td style={{ padding: '0.75rem', borderBottom: isExpanded ? 'none' : '1px solid #f3f4f6' }}>
                        <div style={{ fontWeight: item.status === 'new' ? 600 : 400, fontSize: '0.9rem', color: '#111827' }}>
                          {item.from_name ?? '—'}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.1rem' }}>
                          {item.from_email ?? item.from_phone ?? ''}
                        </div>
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: isExpanded ? 'none' : '1px solid #f3f4f6', fontSize: '0.9rem', color: '#374151', maxWidth: '280px' }}>
                        <div style={{ fontWeight: item.status === 'new' ? 600 : 400 }}>
                          {item.subject ?? '(no subject)'}
                        </div>
                        {!isExpanded && item.body && (
                          <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.15rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.body}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: isExpanded ? 'none' : '1px solid #f3f4f6', fontSize: '0.85rem', color: '#6b7280' }}>
                        {CHANNEL_LABELS[item.channel] ?? item.channel}
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: isExpanded ? 'none' : '1px solid #f3f4f6' }}>
                        <CustomerBadge isNew={item.customer_id === null} />
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: isExpanded ? 'none' : '1px solid #f3f4f6' }}>
                        <StatusBadge status={item.status} />
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: isExpanded ? 'none' : '1px solid #f3f4f6', fontSize: '0.8rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                        {formatDate(item.created_at)}
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: isExpanded ? 'none' : '1px solid #f3f4f6', textAlign: 'right' }}>
                        <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                          {isExpanded ? '▲' : '▼'}
                        </span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td
                          colSpan={colCount}
                          style={{
                            padding: '1rem 1.25rem 1.25rem',
                            borderBottom: '1px solid #f3f4f6',
                            background: '#f9fafb',
                          }}
                        >
                          <div style={{ display: 'flex', gap: '2rem', marginBottom: '0.75rem', fontSize: '0.8rem', color: '#6b7280' }}>
                            {item.from_name && <span><strong>From:</strong> {item.from_name}</span>}
                            {item.from_email && <span><strong>Email:</strong> {item.from_email}</span>}
                            {item.from_phone && <span><strong>Phone:</strong> {item.from_phone}</span>}
                            <span><strong>Channel:</strong> {CHANNEL_LABELS[item.channel] ?? item.channel}</span>
                            <span><strong>Received:</strong> {formatDate(item.created_at)}</span>
                          </div>
                          {item.subject && (
                            <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#111827', marginBottom: '0.5rem' }}>
                              {item.subject}
                            </div>
                          )}
                          <div style={{
                            fontSize: '0.9rem',
                            color: '#374151',
                            whiteSpace: 'pre-wrap',
                            lineHeight: 1.6,
                            background: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.375rem',
                            padding: '0.75rem 1rem',
                          }}>
                            {item.body ?? '(no message body)'}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  )
}
